// @flow

import React, { type Node, useEffect, useState } from 'react';
import styled from 'styled-components';

import searchIconsrc from './searchicon.svg';

import { useOpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import { useUserInputAsRadioButton, useUserInputValue } from '@tectonics/prs/user-input-values';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUserInputValidationError } from '@tectonics/prs/validation';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { getSafePaymentOptionsV2}  from '@tectonics/prs/incoming-payment/get-payment-options';
import { getSafePixPispPaymentOption } from '@tectonics/prs/incoming-payment/payment-option';
import { InputRadioField } from '@tectonics/look-and-feel/uber-v2/molecules/input-radio-field';
import { theme } from '@tectonics/look-and-feel/uber-v2/theme';
import { DisplayBrand } from '@tectonics/look-and-feel/uber-v2/atoms';
import type { BankList } from '@tectonics/prs/incoming-payment/payment-option';

function getFilteredBanklist(bankList: BankList, filter: string) : BankList {
  return bankList.filter(
    f => formatValue(f.name).includes(formatValue(filter)) || filter === ''
  ).slice(0, 3);
}
function formatValue(value: string) : string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function PixPispBankList() : Node {
  const incomingPayment = useOpenedIncomingPayment();
  const isSubmitting = useSubmitting();
  const [selectedBank, setSelectedBank] = useUserInputAsRadioButton('selectedPixPispBank');
  const [, setSelectedPaymentMethod] = useUserInputValue('selectedPaymentMethod');

  useEffect(()=> {
    (async () => {
      if(selectedBank === '' || selectedBank === undefined) {
        await setSelectedPaymentMethod('pix');
      } else {
        await setSelectedPaymentMethod('pix-pisp');
      }
    })();
  }, [selectedBank]);

  const validationError = useUserInputValidationError('selectedPixPispBank');
  const formHasBeenSent = useFormHasBeenSent();

  const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
  const bankList = getSafePixPispPaymentOption(paymentOptions);

  const [filter, setFilter] = useState('');

  return (
    <Root>
      <ChildrenWrapper data-test-name="payment-type-code">
        <SearchWrapper>
          <SearchIcon src={searchIconsrc} alt="Search icon" />
          <InputSearch id="filter"
            name="filter"
            type="text"
            value={filter}
            data-testid="bank-filter"
            data-test-name="bank-filter"
            onChange={event => setFilter(event.target.value)}
            placeholder="Buscar instituição"
          />
        </SearchWrapper>
        <BankSelectionWrapper>
          <HighlightText>Sugestões</HighlightText>
          <BankSelectionGroup>
            {getFilteredBanklist(bankList, filter).map(({ name, value, logo_url }) => {
              let displayBrand = undefined;
              if (logo_url) {
                displayBrand = <DisplayBrand iconSrc={logo_url} iconAlt={name}/>;
              }
              return (
                <InputRadioField
                  key={value}
                  name="card-type"
                  value={value}
                  label={name}
                  checked={selectedBank === value}
                  disabled={isSubmitting}
                  onChange={setSelectedBank}
                  displayBrand={displayBrand}
                />
              );
            })}
          </BankSelectionGroup>
        </BankSelectionWrapper>
        {formHasBeenSent && validationError}
      </ChildrenWrapper>
    </Root>
  );
}

const Root = styled.div`
  margin-top: 20px;
  display: flex;
  align-content: center;
`;

const ChildrenWrapper = styled.div`
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  min-width: 80%;
  margin: auto;
  font-family: ${theme.fontFamily.display};
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  color: #555555;
`;

const HighlightText = styled.span`
  font-size: 0.9em;
  font-weight: 600;
  color: #a1a1a1;
`;

const SearchWrapper = styled.div`
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  display: flex;
`;

const BankSelectionWrapper = styled.div`
  margin-top: 16px;
  padding-inline: 16px;
`;

const BankSelectionGroup = styled.div`
  margin-top: 10px;

  & > * {
    padding-block: 10px;
  }
`;

const SearchIcon = styled.img`
  flex: 0 0 auto;
  height: 100%;
  vertical-align: middle;
  margin: 15px;
`;

const InputSearch = styled.input`
  border:0;
  outline:0;
  width: calc(100% - 20px);
`;
